import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Button, Backdrop, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, startOfWeek } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import WeekPicker from "../Pickers/WeekPicker";
import ExerciseOverview from "./ExerciseOverview.jsx";
import ExerciseInsightsBarChart from "./ExerciseInsightsBarChart.jsx";
import { getExerciseDataBySubjectID } from "../../../services/apiCalls.js";
import { captureAndDownloadCharts } from "../CaptureCharts/captureAndDownloadCharts.js";
import { calculateWeeksAndMonthsWithData } from "../../../utils/filterDataByDate.jsx";

export default function ExerciseComponent({
  selectedUser,
  tabsValue,
  setLoading,
}) {
  const [exerciseData, setExerciseData] = useState([]);

  const exerciseOverview = useRef(null);
  const exerciseInsights = useRef(null);

  const today = new Date();
  const [currentWeek, setCurrentWeek] = useState(today);
  const [todayClickFunc, setTodayClickFunc] = useState(null);

  const { t } = useTranslation("statistics");

  useEffect(() => {
    const fetchUserExerciseData = async () => {
      try {
        const allExerciseData = await getExerciseDataBySubjectID(selectedUser);
        const formattedExerciseData = allExerciseData.map((data) => ({
          date: format(new Date(data.date), "yyyy-MM-dd"),
          light: data.light,
          moderate: data.moderate,
          sedentary: data.sedentary,
          vigorous: data.vigorous,
        }));
        setExerciseData(formattedExerciseData);
      } catch (error) {}
    };
    fetchUserExerciseData();
  }, []);
  const handleTodayClickParent = (func) => {
    if (!todayClickFunc) {
      setTodayClickFunc(() => func);
    }
  };
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });

  const isCurrentWeek =
    weekStart.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime();

  const handleWeekChange = (week) => {
    setCurrentWeek(week);
    // console.log("Updated selected week:", week);
  };

  const weeksMonthsWithData = calculateWeeksAndMonthsWithData(exerciseData);
  // console.log(
  //   "weeksMonthsWithData in exerciseComponent: ",
  //   weeksMonthsWithData
  // );

  const weekStartISO = (d) =>
    format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd");

  const weeksWithDataSet = useMemo(() => {
    const s = new Set();
    for (const row of exerciseData) {
      const day = new Date(row.date); // row.date is 'yyyy-MM-dd'
      s.add(weekStartISO(day));
    }
    return s;
  }, [exerciseData]);

  const hasAnyData = exerciseData.length > 0;
  const todayWeekISO = weekStartISO(today);
  const todayWeekHasData = weeksWithDataSet.has(todayWeekISO);

  const showTodayButton = !isCurrentWeek && (!hasAnyData || todayWeekHasData);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <WeekPicker
          today={today}
          currentCompWeek={currentWeek}
          sendDataToParent={handleTodayClickParent}
          onWeekChange={handleWeekChange}
          weeksWithData={weeksMonthsWithData.weeksWithData}
          monthsWithData={weeksMonthsWithData.monthsWithData}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() =>
              captureAndDownloadCharts(
                "Exercise",
                tabsValue,
                { exerciseOverview, exerciseInsights },
                setLoading
              )
            }
            sx={{
              maxWidth: "280px",
              "&:hover": {
                backgroundColor: "white",
                color: "rgb(2, 178, 175)",
              },
            }}
          >
            {t("btn.downloadAllCharts")}
          </Button>
        </Box>
        {showTodayButton ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Button
              sx={{
                maxWidth: "280px",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "white",
                  color: "rgb(2, 178, 175)",
                },
              }}
              onClick={todayClickFunc}
            >
              {t("btn.today")}
            </Button>
          </Box>
        ) : (
          <Button
            sx={{
              visibility: "hidden",
            }}
          >
            {t("btn.today")}
          </Button>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "row",
              lg: "row",
            },
            gap: "10px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ExerciseOverview
            exerciseData={exerciseData}
            selectedWeek={currentWeek}
            exerciseOverview={exerciseOverview}
          />
          <ExerciseInsightsBarChart
            exerciseData={exerciseData}
            selectedWeek={currentWeek}
            exerciseInsights={exerciseInsights}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
